import { getGenerationFailure } from '@shared/errors'
import type { VideoGeneration } from '@shared/index'
import type { PlaygroundJob, WorkflowAsset } from '@shared/types'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'

import {
  type AppError,
  createAppError,
  errorMessage,
  isAbortError,
  reportError,
  reportGenerationFailure
} from '@/lib/errors'
import { toast } from '@/lib/toast'

const isGenerationDetail = (key: readonly unknown[]) =>
  (key[0] === 'playground' && (key[1] === 'run' || key[1] === 'status')) ||
  (key[0] === 'workflows' && key[1] === 'workspace') ||
  (key[0] === 'video-generations' && key[1] === 'detail')

function reportGenerationData(key: readonly unknown[], data: unknown) {
  if (!data || typeof data !== 'object') return
  if ((key[0] === 'playground' && key[1] === 'run') || (key[0] === 'workflows' && key[1] === 'workspace')) {
    const { assets } = data as { assets: Array<PlaygroundJob | WorkflowAsset> }
    for (const asset of assets ?? []) {
      if (asset.status === 'error' || asset.status === 'moderation_failed') {
        reportGenerationFailure(getGenerationFailure(asset), 'assetId' in asset ? asset.assetId : asset.jobId)
      }
    }
  } else if (key[0] === 'playground' && key[1] === 'status') {
    const job = data as PlaygroundJob
    if (job.status === 'error' || job.status === 'moderation_failed')
      reportGenerationFailure(getGenerationFailure(job), job.jobId)
  } else if (key[0] === 'video-generations' && key[1] === 'detail') {
    const video = data as VideoGeneration
    if (video.failure) reportGenerationFailure(video.failure, video.generationId)
  }
}

export function createAppQueryClient() {
  const failures = new Set<string>()
  const previewFailures = new Set<string>()
  const queryCache = new QueryCache({
    onError: (error, query) => {
      if (isAbortError(error)) return
      if (failures.has(query.queryHash)) return
      failures.add(query.queryHash)
      reportError(error, {
        operation: (error as AppError).operation ?? 'Load application data',
        entityId: typeof query.queryKey[2] === 'string' ? query.queryKey[2] : undefined
      })
      if (isGenerationDetail(query.queryKey))
        toast.error('Could not load generation details', {
          id: `request:${query.queryHash}`,
          description: errorMessage(error)
        })
    },
    onSuccess: (data, query) => {
      if (failures.delete(query.queryHash)) toast.close(`request:${query.queryHash}`)
      reportGenerationData(query.queryKey, data)
      if (query.queryKey[0] === 'image-previews') {
        const failed = (data as { failedKeys?: string[] })?.failedKeys?.length
        if (failed && !previewFailures.has(query.queryHash)) {
          previewFailures.add(query.queryHash)
          reportError(createAppError('Previews unavailable', { code: 'PREVIEW_UNAVAILABLE' }), {
            operation: 'Load image previews'
          })
          toast.info('Some image previews are unavailable', {
            id: 'image-previews-unavailable',
            description: 'Saved images have not been deleted. Try loading their previews again.',
            timeout: 0,
            action: {
              label: 'Retry previews',
              onClick: () => {
                for (const preview of queryCache.getAll()) {
                  if (
                    preview.queryKey[0] === 'image-previews' &&
                    preview.getObserversCount() > 0 &&
                    (preview.state.data as { failedKeys?: string[] })?.failedKeys?.length
                  ) {
                    void preview.fetch().catch(() => undefined)
                  }
                }
              }
            }
          })
        } else if (!failed) {
          if (previewFailures.delete(query.queryHash) && previewFailures.size === 0)
            toast.close('image-previews-unavailable')
        }
      }
    }
  })
  queryCache.subscribe((event) => {
    if (event.type === 'removed') {
      failures.delete(event.query.queryHash)
      if (previewFailures.delete(event.query.queryHash) && previewFailures.size === 0)
        toast.close('image-previews-unavailable')
      if (isGenerationDetail(event.query.queryKey)) toast.close(`request:${event.query.queryHash}`)
    }
  })
  return new QueryClient({
    queryCache,
    mutationCache: new MutationCache({
      onError: (error) => reportError(error, { operation: (error as AppError).operation ?? 'Update application data' })
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          const status = (error as AppError).status
          return failureCount < 1 && !(status && [400, 401, 403, 404, 409, 422].includes(status))
        },
        staleTime: 5000
      }
    }
  })
}
